import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconInfoCircle } from '@sorare/core/src/atoms/icons/system/iconInfoCircle.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, LabelL, LabelM } from '@sorare/core/src/atoms/typography';

import { OddsDescription } from './OddsDescription/index.tsx';
import { OddsTable } from './OddsTable/index.tsx';
import type { DrawAndOdds_cardShardsChest } from './__generated__/index.graphql.ts';

const OddsWarning = styled(Horizontal)`
  color: var(--c-nd-600);
  border: 1px solid var(--c-nd-150);
  padding: var(--double-unit);
  border-radius: var(--unit);
`;

type Props = {
  cardShardsChest: DrawAndOdds_cardShardsChest;
};

export const DrawAndOdds = ({ cardShardsChest }: Props) => {
  return (
    <Vertical center style={{ alignSelf: 'stretch' }}>
      <BrandM className="text-center">
        <FormattedMessage defaultMessage="Types of draws & odds *" />
      </BrandM>
      <LabelL color="var(--c-nd-600)" className="text-center">
        <FormattedMessage defaultMessage="Feeling lucky? Some draws give you better chances at landing top-tier cards." />
      </LabelL>
      <OddsWarning>
        <FontAwesomeIcon icon={iconInfoCircle} />
        <LabelM>
          <FormattedMessage defaultMessage="We’re continuously balancing the system to ensure a fair and engaging experience. This table may be adjusted over time based on data and user feedback." />
        </LabelM>
      </OddsWarning>
      <OddsTable cardShardsChest={cardShardsChest} />
      <OddsDescription cardShardsChest={cardShardsChest} />
    </Vertical>
  );
};

DrawAndOdds.fragments = {
  cardShardsChest: gql`
    fragment DrawAndOdds_cardShardsChest on CardShardsChest {
      id
      ...OddsDescription_cardShardsChest
      ...OddsTable_cardShardsChest
    }
    ${OddsDescription.fragments.cardShardsChest}
    ${OddsTable.fragments.cardShardsChest}
  ` as TypedDocumentNode<DrawAndOdds_cardShardsChest>,
};
